import { useShallow } from 'zustand/react/shallow'
import { useEffect, useState, type MouseEvent } from 'react'
import { version } from '../../../package.json'
import { useNavigate, useLocation } from 'react-router'
import { ref as dbRef, onValue } from 'firebase/database'
import { useAuthStore } from '@/stores/auth'
import { useEffectiveRoles, useEffectivePrimaryRole } from '@/stores/impersonation'
import { useSOSStore } from '@/stores/sos'
import { usePanicStore } from '@/stores/panic'
import { useOrgInvitesStore } from '@/stores/orgInvites'
import { useUIStore } from '@/stores/ui'
import { useSearchModalStore } from '@/stores/searchModal'
import { USER_ROLES } from '@/constants/roles'
import { ROUTES } from '@/constants/routes'
import { rtdb } from '@/lib/firebase'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { prefetchHandlers } from '@/lib/routePrefetch'
import { useHeatIndex } from '@/lib/useHeatIndex'

interface MenuItem {
  route: string
  icon: string
  label: string
}

const FAMILY_LINKED_ROLES = new Set(['family_admin', 'co_carer', 'teacher', 'therapist', 'medical', 'first_responder'])

function useMenuItems(): MenuItem[] {
  const primaryRole = useEffectivePrimaryRole()
  const roles = useEffectiveRoles()
  const managedProfiles = useAuthStore((s) => s.userDoc?.managedProfiles)
  const guardianViewActive = useUIStore((s) => s.guardianViewActive)

  const hasOrgRole = roles.some(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
  const hasFamilyRole = roles.some(r => FAMILY_LINKED_ROLES.has(r.role))
    || (managedProfiles ?? []).some(p => FAMILY_LINKED_ROLES.has(p.role))
  const isDualRole = hasOrgRole && hasFamilyRole

  if (primaryRole === USER_ROLES.FAMILY_ADMIN || primaryRole === USER_ROLES.CO_CARER ||
    (primaryRole === USER_ROLES.SUPER_ADMIN && !hasOrgRole) || (isDualRole && guardianViewActive)) {
    return [
      { route: ROUTES.PROFILES,  icon: 'fa-id-card',            label: 'Safety Profiles' },
      { route: ROUTES.MISSION_CONTROL, icon: 'fa-tower-broadcast', label: 'Mission Control' },
      { route: ROUTES.SOS,       icon: 'fa-triangle-exclamation', label: 'Emergency SOS' },
      { route: ROUTES.LOCATION,  icon: 'fa-location-dot',       label: 'Location' },
      { route: ROUTES.HAZARDS,   icon: 'fa-triangle-exclamation', label: 'Hazard Watch' },
      { route: ROUTES.WEATHER,   icon: 'fa-cloud-sun',            label: 'Weather' },
      { route: ROUTES.QR,        icon: 'fa-qrcode',             label: 'QR Code' },
      { route: ROUTES.SESSIONS,  icon: 'fa-clipboard-list',     label: 'Sessions' },
      { route: ROUTES.COMMUNITY, icon: 'fa-people-group',       label: 'Community' },
      { route: ROUTES.FIND_HELP, icon: 'fa-hand-holding-heart', label: 'Find Help' },
    ]
  }

  if (hasOrgRole || primaryRole === USER_ROLES.ORG_ADMIN || primaryRole === USER_ROLES.STAFF) {
    // Org console is Lumen (opt-in). Keep family shell navigable so staff are not
    // auto-sent to Lumen login when they open davidsbeacon.com.
    return [
      { route: ROUTES.HOME,      icon: 'fa-house',              label: 'Home' },
      { route: ROUTES.ORG,       icon: 'fa-building',           label: 'Organization (Lumen)' },
      { route: ROUTES.COMMUNITY, icon: 'fa-people-group',       label: 'Community' },
      { route: ROUTES.FIND_HELP, icon: 'fa-hand-holding-heart', label: 'Find Help' },
      { route: ROUTES.QR,        icon: 'fa-qrcode',             label: 'QR Code' },
    ]
  }

  return [
    { route: ROUTES.COMMUNITY, icon: 'fa-people-group',       label: 'Community' },
    { route: ROUTES.FIND_HELP, icon: 'fa-hand-holding-heart', label: 'Find Help' },
    { route: ROUTES.HAZARDS,   icon: 'fa-triangle-exclamation', label: 'Hazard Watch' },
    { route: ROUTES.WEATHER,   icon: 'fa-cloud-sun',            label: 'Weather' },
  ]
}

function activeColors(route: string) {
  if (route === ROUTES.SOS || route === ROUTES.ORG_SOS) return { bg: 'bg-danger-100', text: 'text-danger-700', icon: 'text-danger-600' }
  if (route === ROUTES.MISSION_CONTROL) return { bg: 'bg-beacon-100', text: 'text-beacon-700', icon: 'text-beacon-600' }
  if (route === ROUTES.LOCATION || route === ROUTES.ORG_LOCATION) return { bg: 'bg-success-100', text: 'text-success-700', icon: 'text-success-600' }
  if (route === ROUTES.PROFILES) return { bg: 'bg-success-100', text: 'text-success-700', icon: 'text-success-600' }
  if (route === ROUTES.SETTINGS) return { bg: 'bg-settings-100', text: 'text-settings-700', icon: 'text-settings-600' }
  if (route === ROUTES.HAZARDS) return { bg: 'bg-warning-100', text: 'text-warning-700', icon: 'text-warning-600' }
  if (route === ROUTES.WEATHER) return { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'text-sky-600' }
  if (route === ROUTES.COMMUNITY) return { bg: 'bg-community-100', text: 'text-community-700', icon: 'text-community-600' }
  if (route === ROUTES.FIND_HELP) return { bg: 'bg-success-100', text: 'text-success-700', icon: 'text-success-600' }
  return { bg: 'bg-beacon-100', text: 'text-beacon-700', icon: 'text-beacon-600' }
}

interface SidebarContentProps {
  mini: boolean
  pinned: boolean
  onNavigate: (route: string) => void
}

function SidebarContent({ mini, pinned, onNavigate }: SidebarContentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const closeSidebar = useUIStore((s) => s.closeSidebar)
  const menuItems = useMenuItems()
  const openSearch = useSearchModalStore((s) => s.open)
  const [showSignOut, setShowSignOut] = useState(false)
  const isSOSActive = useSOSStore((s) => s.isSOSActive)
  const isPanicActive = usePanicStore((s) => s.isPanicActive)
  const enrollments = useOrgInvitesStore((s) => s.enrollments)
  const [isOrgSOSActive, setIsOrgSOSActive] = useState(false)
  const heat = useHeatIndex()

  const roles = useAuthStore((s) => s.roles())
  const hasOrgRole = roles.some(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)

  useEffect(() => {
    if (!hasOrgRole) {
      setIsOrgSOSActive(false)
      return
    }
    const familyUids = Array.from(new Set(enrollments.map(e => e.familyUid).filter(Boolean)))
    if (familyUids.length === 0) {
      setIsOrgSOSActive(false)
      return
    }

    const stateByUid: Record<string, boolean> = {}
    const unsubs = familyUids.map((familyUid) => onValue(dbRef(rtdb, `sos_status/${familyUid}`), (snap) => {
      const row = snap.exists() ? (snap.val() as { active?: boolean; triggeredBy?: { uid?: string } | null }) : null
      const familyEnrollments = enrollments.filter((e) => e.familyUid === familyUid)
      const hasSosAccess = familyEnrollments.some((e) => e.permissions?.sosAlerts)
      const guardianNotifyEnabled = familyEnrollments.some((e) => !!e.permissions?.notifyOrgOnGuardianSOS)
      const isOrgTriggered = !!row?.triggeredBy?.uid
      stateByUid[familyUid] = !!row?.active && hasSosAccess && (isOrgTriggered || guardianNotifyEnabled)
      setIsOrgSOSActive(Object.values(stateByUid).some(Boolean))
    }))

    return () => unsubs.forEach((fn) => fn())
  }, [enrollments, hasOrgRole])

  const hasSOSAlert = isSOSActive || isOrgSOSActive

  const textCls = `absolute left-14 right-3 top-1/2 -translate-y-1/2 overflow-hidden whitespace-nowrap text-left transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`

  function handleSignOut() {
    setShowSignOut(false)
    closeSidebar()
    navigate(ROUTES.LOGOUT)
  }

  return (
    <>
      {/* Header */}
      <div
        role="button"
        className={[
          'relative h-16 flex items-center flex-shrink-0 overflow-hidden cursor-pointer transition-colors',
          hasSOSAlert
            ? 'bg-danger-600 hover:bg-danger-700 animate-pulse text-white'
            : isPanicActive
              ? 'bg-warning-600 hover:bg-warning-700 animate-pulse text-white'
              : 'bg-white hover:bg-beacon-50 text-beacon-700 border-b border-slate-100',
        ].join(' ')}
        style={{ padding: '0 0.875rem' }}
        onClick={() => {
          if (hasSOSAlert || isPanicActive) { onNavigate(ROUTES.MISSION_CONTROL); return }
          onNavigate(ROUTES.HOME)
        }}
      >
        <img
          src={hasSOSAlert || isPanicActive ? '/brand-mark-inverse.svg' : '/brand-mark.svg'}
          alt="David's Beacon"
          className="absolute left-[0.875rem] top-1/2 -translate-y-1/2 w-7 h-7 drop-shadow-sm"
        />
        <div className={`absolute left-14 right-3 top-1/2 -translate-y-1/2 overflow-hidden text-left transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <p className="font-bold text-base leading-tight whitespace-nowrap">David's Beacon</p>
          <p className={`text-[11px] leading-tight whitespace-nowrap ${hasSOSAlert || isPanicActive ? 'text-white/70' : 'text-beacon-600'}`}>
            {hasSOSAlert ? '⚠ SOS Alert Active' : isPanicActive ? '🔕 Silent Panic Active' : 'Safety & Community App'}
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav className={`flex-1 w-full pt-[5px] pb-2 space-y-0.5 overflow-y-auto overflow-x-hidden ${mini ? '' : 'px-2'}`}>
        {menuItems.map(item => {
          const active = location.pathname === item.route
          const colors = activeColors(item.route)
          const isSosItem = item.route === ROUTES.SOS || item.route === ROUTES.ORG_SOS
          const isMcItem = item.route === ROUTES.MISSION_CONTROL
          const alertPulse = (isSosItem && hasSOSAlert) || (isMcItem && (hasSOSAlert || isPanicActive))
          const panicPulse = isMcItem && isPanicActive && !hasSOSAlert
          const missionAlertClass = isMcItem
            ? 'bg-beacon-50 text-beacon-700 font-semibold hover:bg-beacon-100'
            : panicPulse
              ? 'bg-warning-50 text-warning-700 font-semibold hover:bg-warning-100'
              : 'bg-danger-50 text-danger-600 font-semibold hover:bg-danger-100'
          return (
            <button
              key={item.route}
              {...prefetchHandlers(item.route)}
              onClick={() => onNavigate(item.route)}
              title={mini ? item.label : undefined}
              style={{ padding: '0.625rem 1.125rem' }}
              className={[
                'relative w-full flex items-center transition-colors duration-150',
                mini ? '' : 'rounded-lg',
                active
                  ? `${colors.bg} ${colors.text} font-semibold`
                  : alertPulse
                    ? missionAlertClass
                    : 'text-slate-700 font-medium hover:bg-beacon-100/50 active:bg-beacon-100',
              ].join(' ')}
            >
              <i className={`fa-solid ${item.icon} text-base w-5 text-center flex-shrink-0 ${active ? colors.icon : alertPulse ? (isMcItem ? 'text-beacon-600' : panicPulse ? 'text-warning-700' : 'text-danger-600') : 'text-slate-600'}`} />
              <span className={`${textCls} text-sm leading-snug`}>{item.label}</span>
            </button>
          )
        })}

        <div className="lg:hidden mx-1 my-1.5 border-t border-slate-200" />
        <button
          onClick={openSearch}
          title={mini ? 'Search (Ctrl K)' : undefined}
          style={{ padding: '0.625rem 1.125rem' }}
          className={`lg:hidden relative w-full flex items-center text-slate-500 hover:text-slate-800 hover:bg-slate-50 active:bg-slate-100 transition-colors ${mini ? '' : 'rounded-lg'}`}
        >
          <i className="fa-solid fa-magnifying-glass text-base w-5 text-center flex-shrink-0 text-slate-500" />
          <span className={`${textCls} text-sm font-medium`}>Search</span>
        </button>

        {pinned && (
          <div className="mx-1 my-1.5 border-t border-slate-200" />
        )}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-100">

        {/* Mobile avatar row */}
        <div className="lg:hidden relative flex items-center" style={{ padding: '0.75rem 0.75rem' }}>
          <button onClick={() => onNavigate(ROUTES.SETTINGS)} {...prefetchHandlers(ROUTES.SETTINGS)} className="flex-shrink-0" title="Settings">
            <UserAvatar photoURL={user?.photoURL} displayName={user?.displayName} size="sm" className="hover:ring-2 hover:ring-beacon-400 transition-all" />
          </button>
          <div className={`absolute left-14 right-10 top-1/2 -translate-y-1/2 overflow-hidden text-left transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <p className="text-sm font-medium text-slate-800 truncate">{user?.displayName ?? 'User'}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => setShowSignOut(true)}
            title="Sign Out"
            className={`absolute right-3 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          >
            <i className="fa-solid fa-right-from-bracket text-sm" />
          </button>
        </div>

        {/* Desktop avatar row — visible at lg/xl when floating pill is hidden, disappears at 2xl when pill takes over */}
        <div className="hidden lg:flex 2xl:hidden relative items-center" style={{ padding: '0.75rem 0.75rem' }}>
          <button onClick={() => onNavigate(ROUTES.SETTINGS)} {...prefetchHandlers(ROUTES.SETTINGS)} className="flex-shrink-0" title="Settings">
            <UserAvatar photoURL={user?.photoURL} displayName={user?.displayName} size="sm" className="hover:ring-2 hover:ring-beacon-400 transition-all" />
          </button>
          <div className={`absolute left-14 right-10 top-1/2 -translate-y-1/2 overflow-hidden text-left transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <p className="text-sm font-medium text-slate-800 truncate">{user?.displayName ?? 'User'}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => setShowSignOut(true)}
            title="Sign Out"
            className={`absolute right-3 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-opacity duration-150 ${mini ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          >
            <i className="fa-solid fa-right-from-bracket text-sm" />
          </button>
        </div>
        {/* Heat index pill — weather stays reachable even when the Home slot is showing a PAR storm banner */}
        {heat && (
          <button
            onClick={() => onNavigate(ROUTES.WEATHER)}
            {...prefetchHandlers(ROUTES.WEATHER)}
            className={`${mini ? 'mx-auto mb-1.5 w-9 h-9 rounded-full' : 'mx-3 mb-1.5 w-[calc(100%-1.5rem)] px-2.5 py-1.5 rounded-full'} border text-[11px] font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              heat.level >= 3
                ? 'bg-danger-50 border-danger-300 text-danger-700 hover:bg-danger-100'
                : heat.level === 2
                  ? 'bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100'
                  : 'bg-yellow-50 border-yellow-300 text-yellow-700 hover:bg-yellow-100'
            }`}
            title={`Heat index: ${heat.feelsLike}°C — ${heat.label}. ${heat.advisory}`}
          >
            {mini ? (
              <span className="text-[10px] font-bold leading-none">{heat.feelsLike}°</span>
            ) : (
              <>
                <i className="fa-solid fa-temperature-high text-[10px]" />
                {heat.feelsLike}°C · {heat.label}
              </>
            )}
          </button>
        )}
        <p className="text-[10px] text-slate-400 text-center pb-2">
          v{version}
        </p>
      </div>

      {showSignOut && (
        <ConfirmModal
          icon="fa-solid fa-right-from-bracket text-slate-500"
          iconBg="bg-slate-100"
          title="Sign out?"
          body="You'll need to sign in again to access your profiles and alerts."
          confirmLabel="Sign Out"
          confirmClass="bg-danger-500 hover:bg-danger-600"
          onCancel={() => setShowSignOut(false)}
          onConfirm={handleSignOut}
        />
      )}
    </>
  )
}

export function Sidebar() {
  const navigate = useNavigate()
  const { sidebarOpen, sidebarExpanded, closeSidebar, toggleExpanded } = useUIStore(useShallow((s) => ({ sidebarOpen: s.sidebarOpen, sidebarExpanded: s.sidebarExpanded, closeSidebar: s.closeSidebar, toggleExpanded: s.toggleExpanded })))

  function handleNavigate(route: string) {
    navigate(route)
    closeSidebar()
  }

  const mini = !sidebarExpanded

  function handleDesktopBlankClick(e: MouseEvent<HTMLElement>) {
    const target = e.target as HTMLElement
    if (target.closest('button, a, input, textarea, select, [role="button"]')) return
    toggleExpanded()
  }

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[2000] bg-black/40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed top-0 left-0 bottom-0 z-[2001] w-52 bg-white shadow-xl flex flex-col overflow-hidden lg:hidden transition-transform duration-250 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent mini={false} pinned={false} onNavigate={handleNavigate} />
      </aside>

      {/* Desktop pinned sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 bottom-0 z-40 bg-white overflow-hidden transition-[width] duration-300 cursor-pointer ${
          sidebarExpanded ? 'w-52' : 'w-14'
        }`}
        onClick={handleDesktopBlankClick}
      >
        <SidebarContent mini={mini} pinned onNavigate={handleNavigate} />
      </aside>
    </>
  )
}
